
import { useState, useEffect, RefObject } from 'react';

interface UsePortfolioSectionStickyProps {
  sectionRef: RefObject<HTMLDivElement>;
  headerRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  isMobile: boolean;
  showFilterOptions?: boolean;
  showMobileSearch?: boolean;
}

export function usePortfolioSectionSticky({
  sectionRef,
  headerRef,
  contentRef,
  isMobile,
  showFilterOptions = false,
  showMobileSearch = false
}: UsePortfolioSectionStickyProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [stickyTopPosition, setStickyTopPosition] = useState(0);

  // Update header height when filter options OR search state changes
  useEffect(() => {
    const calculateHeaderHeight = () => {
      const headerSection = document.querySelector('.header-section');
      if (headerSection) {
        const height = headerSection.getBoundingClientRect().height;
        setHeaderHeight(height);
        
        // Desktop: shift down by additional 2px when filter menu is open
        const desktopFilterAdjustment = !isMobile && showFilterOptions ? 2 : 0;
        
        // Mobile adjustments for different combinations
        let mobileAdjustment = 0;
        if (isMobile) {
          if (showFilterOptions && showMobileSearch) {
            // Both filter and search are open
            mobileAdjustment = 35 + 20; // Increased adjustment when both are open (+20px)
          } else if (showFilterOptions && !showMobileSearch) {
            // Only filter is open, search is closed
            mobileAdjustment = -10; // Keep adjustment when only filter is open
          } else if (!showFilterOptions && showMobileSearch) {
            // Only search is open, filter is closed
            mobileAdjustment = 20; // Add 20px adjustment when only search is open
          } else if (!showFilterOptions && !showMobileSearch) {
            // Both filter and search are closed
            mobileAdjustment = -15; // Keep original adjustment when both are closed
          }
          
          // Add additional 10px for mobile sticky position
          mobileAdjustment += 10;
        }
        
        // Base buffer values
        const buffer = isMobile ? 
          (showFilterOptions ? 56 : 59) + (showMobileSearch ? 5 : 0) :  // Mobile base adjustment
          (showFilterOptions ? 50 : 55);   // Desktop base adjustment
        
        // Apply the final position with all adjustments
        setStickyTopPosition(height + buffer + desktopFilterAdjustment + mobileAdjustment);
      }
    };

    // Initial calculation
    calculateHeaderHeight();
    
    // Set up multiple timers to ensure position is properly recalculated
    // This handles edge cases where DOM updates happen at different times
    const timers = [
      setTimeout(calculateHeaderHeight, 50),
      setTimeout(calculateHeaderHeight, 150),
      setTimeout(calculateHeaderHeight, 300)
    ];
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [showFilterOptions, isMobile, showMobileSearch]);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !headerRef.current || !contentRef.current) return;
      
      const sectionRect = sectionRef.current.getBoundingClientRect();
      
      // Updated fallback values with search bar consideration
      const searchAdjustment = showMobileSearch ? 10 : 0;
      const mobileAdjustment = isMobile ? 10 : 0; // Additional 10px for mobile sticky threshold
      
      // Handle specific combinations differently
      let combinationAdjustment = 0;
      
      if (isMobile) {
        if (showFilterOptions && showMobileSearch) {
          // Both filter and search are open
          combinationAdjustment = 20;
        } else if (showFilterOptions && !showMobileSearch) {
          // Only filter is open
          combinationAdjustment = 5;
        } else if (!showFilterOptions && showMobileSearch) {
          // Only search is open
          combinationAdjustment = 20;
        } else {
          // Both closed
          combinationAdjustment = 0; // Reset to 0 when both are closed
        }
      }
      
      const stickyThreshold = stickyTopPosition > 0 ? 
        stickyTopPosition : 
        (isMobile ? 
          (showFilterOptions ? 176 : 180) + searchAdjustment + mobileAdjustment + combinationAdjustment : 
          (showFilterOptions ? 190 : 205)); // Desktop expanded reduced by 10px
      
      // Set sticky state based on scroll position and section position
      const shouldBeSticky = sectionRect.top <= stickyThreshold;
      
      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
      }
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // Force check when showMobileSearch changes
    if (isMobile) {
      handleScroll();
      
      // Add additional checks with slight delay to ensure position is recalculated
      // after DOM updates from search state changes
      const timers = [
        setTimeout(handleScroll, 50),
        setTimeout(handleScroll, 150),
        setTimeout(handleScroll, 300)
      ];
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        timers.forEach(timer => clearTimeout(timer));
      };
    }
    
    // Force initial check
    handleScroll();
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isMobile, sectionRef, headerRef, contentRef, isSticky, stickyTopPosition, showFilterOptions, showMobileSearch]);

  return { 
    isSticky, 
    headerHeight,
    stickyTopPosition 
  };
}
